import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Utensils, CalendarDays, Users } from 'lucide-react';
import { Button } from '../components/Button';
const highlights = [{
  title: 'Chicken Jambalaya',
  desc: 'Our signature saffron rice with turkey bacon and fresh seasonal vegetables.',
  price: '$11.00 - $14.00',
  img: 'https://cdn.magicpatterns.com/uploads/ckSRkmGFpjvdEKRDEFbPST/Chicken_Jambalaya.png'
}, {
  title: 'Hibiscus Lemonade',
  desc: 'Refreshing, tart, and floral. The perfect vibrant pairing for any meal.',
  price: '$4.00',
  img: 'https://images.unsplash.com/photo-1499638673689-79a0b5115d87?auto=format&fit=crop&q=80&w=800'
}, {
  title: 'Roasted Eggplant',
  desc: 'With black eyed peas, barley risotto, mushroom and parmesan.',
  price: 'Market Price',
  img: 'https://cdn.magicpatterns.com/uploads/1ZxogF4taKXLuanioFWzxC/Eggplant.png'
}];
export function Home() {
  return <main className="min-h-screen w-full bg-cream" data-id="element-71">
      {/* HERO SECTION */}
      <section className="grid lg:grid-cols-2 min-h-[90vh] pt-20 lg:pt-0" data-id="element-72">
        {/* Text panel */}
        <div className="bg-forest relative overflow-hidden flex items-center order-2 lg:order-1" data-id="element-73">
          <div className="absolute inset-0 opacity-5 bg-pattern-mudcloth" data-id="element-74" />
          <div className="relative z-10 px-6 sm:px-10 lg:px-16 py-16 lg:py-0 w-full" data-id="element-75">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="max-w-xl mx-auto lg:mx-0" data-id="element-76">
              <span className="inline-block py-1 px-3 bg-saffron text-charcoal font-bold tracking-widest uppercase text-sm mb-6" data-id="element-77">
                Catering Only
              </span>
              <h1 className="text-5xl md:text-7xl font-serif text-cream leading-[1.1] mb-6" data-id="element-78">
                Vibrant Flavors.
                <br data-id="element-79" />
                <span className="text-terracotta italic" data-id="element-80">
                  Unforgettable
                </span>{' '}
                Events.
              </h1>
              <p className="font-serif font-semibold text-xl md:text-2xl text-cream mb-10 leading-relaxed" data-id="element-81">
                Farmer's market sourced African and Mediterranean inspired
                cuisine. We are a catering-only kitchen dedicated to bringing
                unforgettable flavors to your events, gatherings, and
                celebrations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-82">
                <Button size="lg" onClick={() => document.getElementById('inquiry')?.scrollIntoView({
                behavior: 'smooth'
              })} data-id="element-83">
                  Inquire About Catering
                </Button>
                <Link to="/menu" data-id="element-84">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-cream text-cream hover:bg-cream hover:text-charcoal" data-id="element-85">
                    View Menu
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Image panel */}
        <div className="order-1 lg:order-2 min-h-[40vh] lg:min-h-0" data-id="element-86">
          <img src="https://cdn.magicpatterns.com/uploads/2qHTd3NxgTMLY6wiagDR42/be3f76f4-714b-4efe-b8fc-94d27cb2a487.jpg" alt="Chef in the kitchen with fresh market produce and spices" className="w-full h-full object-cover" data-id="element-87" />
        </div>
      </section>

      {/* STORY / IDENTITY STRIP */}
      <section className="py-20 bg-charcoal text-cream relative overflow-hidden" data-id="element-88">
        <div className="absolute inset-0 opacity-10 bg-pattern-mudcloth" data-id="element-89" />
        <div className="container mx-auto px-4 md:px-8 relative z-10" data-id="element-90">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-91">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-6" data-id="element-92">
              <h2 className="text-4xl md:text-5xl font-serif text-saffron" data-id="element-93">
                Our Roots
              </h2>
              <p className="text-lg text-cream/80 leading-relaxed text-balance" data-id="element-94">
                Born from a passion for rich, earthy spices and fresh, seasonal
                ingredients, Radio Africa & Kitchen is a celebration of culinary
                heritage. We blend traditional African flavor profiles with
                modern techniques to create dishes that are as visually stunning
                as they are delicious.
              </p>
              <p className="text-lg text-cream/80 leading-relaxed text-balance" data-id="element-95">
                As a dedicated catering kitchen, our focus is entirely on
                crafting the perfect menu for your specific event.
              </p>
            </motion.div>
            <div className="grid grid-cols-2 gap-4" data-id="element-96">
              <img src="https://cdn.magicpatterns.com/uploads/tWRmHBSgFjZuqS7m9VHYn6/5d5b1fdd-ae0c-4af0-ba7a-8b5bc2f081f1.jpg" alt="Chef garnishing plated catering dishes in the kitchen" className="w-full h-64 object-cover object-left rounded-tl-3xl rounded-br-3xl" data-id="element-97" />
              <img src="https://cdn.magicpatterns.com/uploads/hB17nuu2dxbEcEZZTmY2Ag/Shrimp.png" alt="Spiced shrimp over couscous with sautéed greens" className="w-full h-64 object-cover rounded-tr-3xl rounded-bl-3xl mt-8" data-id="element-98" />
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-24 bg-cream" data-id="element-99">
        <div className="container mx-auto px-4 md:px-8" data-id="element-100">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6" data-id="element-101">
            <div className="max-w-2xl" data-id="element-102">
              <h2 className="text-4xl md:text-5xl font-serif text-charcoal mb-4" data-id="element-103">
                Signature Selections
              </h2>
              <p className="text-charcoal/70 text-lg" data-id="element-104">
                A taste of what everyone is talking about. Perfect for your next
                gathering.
              </p>
            </div>
            <Link to="/menu" className="group flex items-center gap-2 text-terracotta font-semibold uppercase tracking-wider text-sm hover:text-charcoal transition-colors" data-id="element-105">
              See Full Menu
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-106" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-107">
            {highlights.map((item, i) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group cursor-pointer" data-id="element-108">
                <div className="relative h-80 mb-6 overflow-hidden bg-charcoal/5" data-id="element-109">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-110" />
                </div>
                <h3 className="text-2xl font-serif text-charcoal mb-2 group-hover:text-terracotta transition-colors" data-id="element-111">
                  {item.title}
                </h3>
                <p className="text-charcoal/70 leading-relaxed" data-id="element-112">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CATERING INQUIRY */}
      <section id="inquiry" className="py-24 bg-charcoal text-cream relative overflow-hidden" data-id="element-113">
        <div className="absolute right-0 top-0 w-1/2 h-full opacity-5 bg-pattern-mudcloth" data-id="element-114" />
        <div className="container mx-auto px-4 md:px-8 relative z-10" data-id="element-115">
          <div className="max-w-4xl mx-auto bg-forest p-8 md:p-12 rounded-none border border-cream/10 shadow-2xl" data-id="element-116">
            <div className="text-center mb-12" data-id="element-117">
              <span className="text-saffron font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-118">
                Let's Plan Together
              </span>
              <h2 className="text-4xl md:text-5xl font-serif mb-6" data-id="element-119">
                Inquire About Catering
              </h2>
              <p className="text-cream/80 text-lg max-w-2xl mx-auto" data-id="element-120">
                We specialize in creating custom menus for events of all sizes.
                Fill out the form below or contact us directly to start planning
                your perfect spread.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-12 mb-12 border-b border-cream/10 pb-12" data-id="element-121">
              <div className="flex flex-col items-center text-center gap-3" data-id="element-122">
                <div className="w-12 h-12 rounded-full bg-saffron/20 flex items-center justify-center text-saffron" data-id="element-123">
                  <Utensils size={24} data-id="element-124" />
                </div>
                <h4 className="font-serif text-xl" data-id="element-125">Custom Menus</h4>
                <p className="text-sm text-cream/60" data-id="element-126">
                  Tailored to your dietary needs and preferences.
                </p>
              </div>
              <div className="flex flex-col items-center text-center gap-3" data-id="element-127">
                <div className="w-12 h-12 rounded-full bg-terracotta/20 flex items-center justify-center text-terracotta" data-id="element-128">
                  <Users size={24} data-id="element-129" />
                </div>
                <h4 className="font-serif text-xl" data-id="element-130">Any Size Event</h4>
                <p className="text-sm text-cream/60" data-id="element-131">
                  From intimate 10-person dinners to 200+ guests.
                </p>
              </div>
              <div className="flex flex-col items-center text-center gap-3" data-id="element-132">
                <div className="w-12 h-12 rounded-full bg-cream/10 flex items-center justify-center text-cream" data-id="element-133">
                  <CalendarDays size={24} data-id="element-134" />
                </div>
                <h4 className="font-serif text-xl" data-id="element-135">Flexible Booking</h4>
                <p className="text-sm text-cream/60" data-id="element-136">
                  Secure your date early for the best availability.
                </p>
              </div>
            </div>

            <form className="space-y-6" onSubmit={e => e.preventDefault()} data-id="element-137">
              <div className="grid md:grid-cols-2 gap-6" data-id="element-138">
                <div className="space-y-2" data-id="element-139">
                  <label className="text-sm font-medium text-cream/80" data-id="element-140">
                    Name
                  </label>
                  <input type="text" className="w-full bg-charcoal border border-cream/20 p-3 text-cream focus:outline-none focus:border-saffron transition-colors" placeholder="Jane Doe" data-id="element-141" />
                </div>
                <div className="space-y-2" data-id="element-142">
                  <label className="text-sm font-medium text-cream/80" data-id="element-143">
                    Email
                  </label>
                  <input type="email" className="w-full bg-charcoal border border-cream/20 p-3 text-cream focus:outline-none focus:border-saffron transition-colors" placeholder="jane@example.com" data-id="element-144" />
                </div>
                <div className="space-y-2" data-id="element-145">
                  <label className="text-sm font-medium text-cream/80" data-id="element-146">
                    Event Date
                  </label>
                  <input type="date" className="w-full bg-charcoal border border-cream/20 p-3 text-cream focus:outline-none focus:border-saffron transition-colors" data-id="element-147" />
                </div>
                <div className="space-y-2" data-id="element-148">
                  <label className="text-sm font-medium text-cream/80" data-id="element-149">
                    Guest Count
                  </label>
                  <input type="number" className="w-full bg-charcoal border border-cream/20 p-3 text-cream focus:outline-none focus:border-saffron transition-colors" placeholder="50" data-id="element-150" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-151">
                <label className="text-sm font-medium text-cream/80" data-id="element-152">
                  Event Details
                </label>
                <textarea rows={4} className="w-full bg-charcoal border border-cream/20 p-3 text-cream focus:outline-none focus:border-saffron transition-colors resize-none" placeholder="Tell us about your event, dietary restrictions, and what you're looking for..." data-id="element-153"></textarea>
              </div>
              <Button type="submit" size="lg" className="w-full bg-saffron text-charcoal hover:bg-saffron/90" data-id="element-154">
                Send Inquiry
              </Button>
            </form>

            <div className="mt-12 text-center" data-id="element-155">
              <p className="text-cream/60 text-sm mb-2" data-id="element-156">
                Or reach us directly at:
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8 font-serif text-lg" data-id="element-157">
                <a href="mailto:radioafricak@yahoo.com" className="hover:text-saffron transition-colors" data-id="element-158">
                  radioafricak@yahoo.com
                </a>
                <span className="hidden sm:inline text-terracotta" data-id="element-159">•</span>
                <a href="tel:+14154202486" className="hover:text-saffron transition-colors" data-id="element-160">
                  +1 415 420 2486
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}